/**
 * Staff client switcher — which client workspace a Beatly staff session acts in.
 *
 * Staff (BO admin token, enrolled CM, beatly.com login) land in the house team.
 * Picking a client here sends `X-Cowork-Team` on every Cowork API request, so
 * Brand OS, Reports, Media plans and Kickoff read and write THAT client's
 * workspace (backend: middleware/staffScope.ts). The choice is per browser and
 * survives reloads; the backend ignores the header for non-staff sessions.
 *
 * Kept import-free so the API client can use it without a cycle.
 */

import { isEmbedMode } from '@/utils/urlParams';

const KEY = 'cowork.staffTeam';
export const HOUSE = 'house';

/** Selected client team id, `'house'` for the house workspace, or null (never chosen). */
/**
 * Only inside the back office (embed mode). In the omni app itself the platform's own team switch (top right) picks
 * the client and mints a token for it, so a second picker — and a stored header that could disagree with it — is not
 * used there. A BO admin session cannot switch teams on the platform, so the BO hands the client over instead.
 */
export function getStaffScopeTeam(): string | null {
  if (!isEmbedMode()) return null;
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
}

export function setStaffScopeTeam(teamId: string | null): void {
  try {
    if (teamId) localStorage.setItem(KEY, teamId); else localStorage.removeItem(KEY);
  } catch { /* storage unavailable: the session stays in its own team */ }
}

/**
 * A hosting shell (the Beatly BO frame) can hand the client over in the URL:
 * `?scopeTeam=<team id>` (or `house`). Adopted once per page load, before the
 * first API call, and then stored like a manual choice.
 */
export function adoptStaffScopeFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get('scopeTeam');
    if (!t) return;
    setStaffScopeTeam(t.trim() || null);
    url.searchParams.delete('scopeTeam');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* no window / bad URL: keep the stored choice */ }
}

/** Header(s) to add to every Cowork API request. Empty for non-staff or no choice. */
export function staffScopeHeaders(): Record<string, string> {
  const t = getStaffScopeTeam();
  return t ? { 'X-Cowork-Team': t } : {};
}

/** True when the selection is a real Beatly client team (not house, not unset). */
export function isClientTeamId(id: string | null | undefined): id is string {
  return !!id && id !== HOUSE && /-tea$/.test(id) && !id.startsWith('admin-');
}
